import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, MapPin, Clock, Users, Heart } from "lucide-react";
import { motion } from "framer-motion";

interface CareersPageProps {
  onNavigate?: (page: string) => void;
}

export default function CareersPage({ onNavigate }: CareersPageProps) {
  const handleNavigation = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    } else {
      window.location.hash = `#/${page}`;
    }
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="max-w-3xl mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Badge variant="secondary" className="mb-4">
            Join Our Team
          </Badge>
          <h1 className="mb-6">
            Build the future of patient-led cannabis reviews
          </h1>
          <p className="text-xl text-muted-foreground">
            We're building something special at CannaBias — a transparent, patient-first platform that empowers the UK medical cannabis community. Join us in making a difference.
          </p>
        </motion.div>

        {/* Values Section */}
        <motion.div 
          className="grid md:grid-cols-3 gap-6 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <Card className="p-6">
            <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center mb-4">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <h3 className="mb-2">Patient-First</h3>
            <p className="text-muted-foreground">
              Everything we do is guided by what's best for patients and the community.
            </p>
          </Card>
          
          <Card className="p-6">
            <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center mb-4">
              <span className="text-2xl">🔍</span>
            </div>
            <h3 className="mb-2">Transparency</h3>
            <p className="text-muted-foreground">
              We believe in open, honest communication and clear decision-making.
            </p>
          </Card>
          
          <Card className="p-6">
            <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center mb-4">
              <span className="text-2xl">🤝</span>
            </div>
            <h3 className="mb-2">Community-Driven</h3>
            <p className="text-muted-foreground">
              Built by patients, for patients — your voice shapes our platform.
            </p>
          </Card>
        </motion.div>

        {/* Open Positions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h2 className="mb-8">Open Positions</h2>
          
          <Card className="p-8 hover:border-primary/50 transition-colors cursor-pointer group" onClick={() => handleNavigation("ambassador-role")}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3>CannaBias Ambassador</h3>
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                        9 positions available
                      </Badge>
                    </div>
                    <p className="text-muted-foreground mb-4">
                      An essential member of our team, linking different perspectives, raising awareness and demonstrating a commitment to transparency.
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>UK wide</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Flexible hours</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        <span>Volunteer position</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <Button className="gap-2 group-hover:gap-3 transition-all lg:self-start">
                View Role
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}